import { useState } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ps-design-system/components/Dialog";
import { Text } from "@ps-design-system/components/Text";

import type { OwnerListing } from "../../../services/listings.service";

import { DateBasedPricingListingOption } from "./DateBasedPricingListingOption";

type DateBasedPricingListingDialogProps = {
  listings: OwnerListing[];
  onClose: () => void;
  onContinue: (listing: OwnerListing) => void;
  open: boolean;
};

/**
 * Asks a host with several spaces which one to set date pricing for. The
 * first space is selected by default.
 */
const DateBasedPricingListingDialog = ({
  listings,
  onClose,
  onContinue,
  open,
}: DateBasedPricingListingDialogProps) => {
  const [selectedListingId, setSelectedListingId] = useState<string>();
  const selectedListing =
    listings.find((listing) => listing._id === selectedListingId) ??
    listings[0];

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
    }
  };

  const handleContinue = () => {
    if (selectedListing) {
      onContinue(selectedListing);
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent size="sm" testId="date-based-pricing-dialog">
        <DialogHeader showCloseButton>
          <DialogTitle className="tw-m-0">
            <FormattedMessage
              id="host_hub.date_based_pricing_dialog.title"
              defaultMessage="Set date pricing"
            />
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="tw-flex tw-flex-col tw-gap-8">
          <Text
            className="tw-text-gray-900"
            id="date-based-pricing-dialog-select-space"
            typeface="body-2-m"
          >
            <FormattedMessage
              id="host_hub.date_based_pricing_dialog.select_space"
              defaultMessage="Select space"
            />
          </Text>
          <div
            aria-labelledby="date-based-pricing-dialog-select-space"
            className="tw-flex tw-flex-col tw-gap-8 tw-overflow-y-auto tw-max-h-[214px]"
            data-testid="date-based-pricing-dialog-listings"
            role="radiogroup"
          >
            {listings.map((listing) => (
              <DateBasedPricingListingOption
                isSelected={listing._id === selectedListing?._id}
                key={listing._id}
                listing={listing}
                onSelect={setSelectedListingId}
              />
            ))}
          </div>
        </DialogBody>
        <DialogFooter className="sm:tw-px-32 sm:tw-py-32">
          <div className="tw-grid tw-w-full tw-grid-cols-2 tw-items-center tw-gap-12">
            <Button
              className="tw-justify-self-start"
              onClick={onClose}
              testId="date-based-pricing-dialog-cancel-button"
              variant="underline-m"
            >
              <FormattedMessage
                id="host_hub.date_based_pricing_dialog.cancel"
                defaultMessage="Cancel"
              />
            </Button>
            <Button
              fullWidth
              onClick={handleContinue}
              testId="date-based-pricing-dialog-continue-button"
            >
              <FormattedMessage
                id="host_hub.date_based_pricing_dialog.continue"
                defaultMessage="Continue"
              />
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { DateBasedPricingListingDialog };
